import React, { useState, useEffect, useCallback, useRef } from 'react';
import supabase from '../../config/supabase';
import CustomModal from '../CustomModal';
import VendorContactModal from './VendorContactModal';
import OrdersPage from '../OrdersPage';
import CompletedCountsList from './CompletedCountsList';
import CountOrderReview from './CountOrderReview';
import DraftOrderReview from './DraftOrderReview';
import SavingsReviewContainer from './SavingsReviewContainer';
import ShoppingListView from '../ShoppingListView';
import VendorEditor from '../VendorEditor';
import { fetchOpenPlans, fetchOpenPlansForSessions } from '../../services/purchasingPlanService';
import { fetchShoppingLists, fetchShoppingList } from '../../services/shoppingListService';
import { fetchSessionHubStatuses, listDraftPOsForSessions } from '../../services/orderingHubService';
import { buildPurchasingWorkQueue, ACTIVITY_KIND, ACTIVITY_STAGE, AMOUNT_KIND, formatMoney, amountLabel } from '../../services/purchasingActivityService';
import PurchaseOrderCard from '../orders/PurchaseOrderCard';
import { ORDER_STATUS_STYLES, BTN_PRIMARY, BTN_SECONDARY, BTN_DISABLED, BTN_DANGER, FOCUS_RING_CSS } from './orderingStyles';
import {
  listAllPOsForHub, listOrderingProblems, businessHoursElapsed,
  listDraftOrders, listLatestSentPOs, listReceivedPOs,
} from '../../services/orderingHubService';
import { applyActiveFilter } from '../../utils/sessionLifecycle';
import {
  formatPoCurrency, createAmendmentDraft, markPurchaseOrderReceived,
  resolveUserProfileCompactName,
  deleteDraftPurchaseOrder, updateDraftPurchaseOrder,
  cancelPurchaseOrder, voidPurchaseOrder, archivePurchaseOrder, unarchivePurchaseOrder,
  createReplacementDraft, duplicatePurchaseOrderAsDraft,
} from '../../services/purchaseOrderService';
import { printPurchaseOrder } from '../orders/printPurchaseOrder';
import { formatLocalDateTime } from '../../utils/deliveryDatePresets';

const STATUS_CHIPS = [
  { value: 'all', label: 'All' },
  { value: 'drafts', label: 'Drafts' },
  { value: 'sent', label: 'Sent' },
  { value: 'received', label: 'Received' },
  { value: 'canceled', label: 'Canceled / Voided' },
  { value: 'archived', label: 'Archived' },
];

const SORT_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'status', label: 'Status' },
  { value: 'amount', label: 'Order Amount' },
  { value: 'plcat', label: 'P&L Category' },
  { value: 'itemcat', label: 'Item Category' },
];

const CANCEL_REASONS = [
  '', 'Sent by mistake', 'Vendor unavailable', 'Delivery unavailable', 'Duplicate order', 'Other',
];
const VOID_REASONS = [
  '', 'Entered by mistake', 'Duplicate', 'Vendor did not fulfill', 'Other',
];
const FOLLOW_UP_TRIGGERS = new Set(['Vendor unavailable', 'Delivery unavailable']);

// Use the shared status palette; override the draft label for hub context
// (shows "Needs Review" to prompt action instead of the generic "Draft").
const STATUS_STYLES = {
  ...ORDER_STATUS_STYLES,
  draft: { ...ORDER_STATUS_STYLES.draft, label: 'Needs Review' },
};

const SOURCE_TYPE_LABELS = {
  count_complete: 'From inventory count',
  report_preview: 'From order report',
  manual: 'Manual order',
  amendment: 'Amendment',
};
function fmtSourceType(t) { return SOURCE_TYPE_LABELS[t] || (t ? String(t).replace(/_/g, ' ') : 'Manual'); }

function derivePoStatus(po) {
  const raw = po.delivery_status || '';
  if ([
    'draft', 'blocked_no_email', 'blocked', 'bounced', 'failed', 'error', 'sending',
    'pending_approval', 'revised', 'received', 'closed', 'canceled', 'replaced', 'voided', 'confirmed',
  ].includes(raw)) return raw;
  if (po.clicked_at) return 'clicked';
  if (po.opened_at) return 'opened';
  if (po.delivered_at || raw === 'delivered') return 'delivered';
  return raw || 'sent';
}

const fmt = formatPoCurrency;
const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return String(v); }
};
const fmtDelivDate = (v) => {
  if (!v) return null;
  try { return new Date(`${v}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return String(v); }
};
const fmtAgo = (v) => {
  if (!v) return '';
  const h = Math.floor((Date.now() - new Date(v).getTime()) / 3600000);
  return h < 48 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};

/**
 * Get the primary item location from a PO's embedded purchase_order_items array.
 * Items are sorted by sort_order; location of the first item is used as the
 * P&L-category sort proxy.
 *
 * Why a proxy? The true P&L category is derived from inventory subcategory via
 * pnl_category_mappings and is not stored on purchase_order_items. Sorting by the
 * first item's inventory location (e.g. "Bar", "Kitchen") is an accepted approximation
 * because items in the same location typically map to the same P&L bucket.
 * A denormalised pnl_category snapshot on purchase_order_items (follow-up #503)
 * would replace this proxy with the real value.
 *
 * Falls back to vendor_name_snapshot for POs with no line items.
 */
function primaryItemLocation(po) {
  const items = Array.isArray(po.purchase_order_items) ? po.purchase_order_items : [];
  if (items.length === 0) return (po.vendor_name_snapshot || '').toLowerCase();
  const sorted = [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  return (sorted[0]?.location || sorted[0]?.description || '').toLowerCase();
}

/**
 * Get the primary item description for "item category" sorting.
 * Uses the alphabetically-first description as a stable proxy for item category,
 * since item_category is not stored on purchase_order_items rows.
 * Falls back to vendor_name_snapshot for POs with no line items.
 */
function primaryItemDescription(po) {
  const items = Array.isArray(po.purchase_order_items) ? po.purchase_order_items : [];
  if (items.length === 0) return (po.vendor_name_snapshot || '').toLowerCase();
  const descs = items.map((it) => (it.description || '').toLowerCase()).filter(Boolean);
  return descs.sort()[0] || (po.vendor_name_snapshot || '').toLowerCase();
}

function applySortClient(pos, sortKey, sortDir) {
  const asc = sortDir === 'asc';
  const sorted = [...pos];
  sorted.sort((a, b) => {
    let va, vb;
    switch (sortKey) {
      case 'vendor':
        va = (a.vendor_name_snapshot || '').toLowerCase();
        vb = (b.vendor_name_snapshot || '').toLowerCase();
        break;
      case 'status':
        va = a.delivery_status || '';
        vb = b.delivery_status || '';
        break;
      case 'amount':
        va = parseFloat(a.total) || 0;
        vb = parseFloat(b.total) || 0;
        break;
      case 'plcat':
        // Sort by primary item location (inventory location ≈ P&L bucket proxy)
        va = primaryItemLocation(a);
        vb = primaryItemLocation(b);
        break;
      case 'itemcat':
        // Sort by alphabetically-first item description
        va = primaryItemDescription(a);
        vb = primaryItemDescription(b);
        break;
      case 'date':
      default:
        va = a.created_at || '';
        vb = b.created_at || '';
        break;
    }
    if (va < vb) return asc ? -1 : 1;
    if (va > vb) return asc ? 1 : -1;
    // tie-break: always newest first
    const ta = a.created_at || '';
    const tb = b.created_at || '';
    return ta > tb ? -1 : ta < tb ? 1 : 0;
  });
  return sorted;
}

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

export default function OrderingHub({ storeId, storeName, storeAddress = '', userEmail = '', userName = '' }) {
  // A count that finishes with multi-vendor items hands off here with
  // ?savings=<plan id>; landing directly on the worksheet is the point of the
  // link, so the initial view honours it.
  const initialSavingsPlanId = (() => {
    try {
      return new URLSearchParams(window.location.search).get('savings') || null;
    } catch (_) { return null; }
  })();
  const [view, setView] = useState(initialSavingsPlanId ? 'savings-review' : 'landing');
  const [savingsPlanId, setSavingsPlanId] = useState(initialSavingsPlanId);
  const [openSavingsCount, setOpenSavingsCount] = useState(0);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 680);
  const [authReady, setAuthReady] = useState(false);
  const [vendorEditorTargetId, setVendorEditorTargetId] = useState(null);
  const [vendorEditorReturnView, setVendorEditorReturnView] = useState('landing');
  // Purchasing workspace top-level tab: Active vs History (inside landing)
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'history'
  const [activeSearch, setActiveSearch] = useState('');

  // Unified PO list state (History tab)
  const [allPOs, setAllPOs] = useState([]);
  const [hubPage, setHubPage] = useState(0);
  const [hubHasMore, setHubHasMore] = useState(true);
  const [posLoading, setPosLoading] = useState(false);
  const [posError, setPosError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [hasLoadedInitialPage, setHasLoadedInitialPage] = useState(false);

  // Search / sort / filter controls (History)
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [hubStatusFilter, setHubStatusFilter] = useState('all');

  // Needs Attention (raw)
  const [problems, setProblems] = useState({ staleDrafts: [], bouncedOrFailed: [], missingEmail: [], stalledPOs: [] });
  // Purchasing work queue (Active tab) — unified read model
  const [workQueue, setWorkQueue] = useState({ needsAttention: [], ready: [], inProgress: [], recent: [], activities: [] });
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState('');
  const [workQueueRaw, setWorkQueueRaw] = useState({ plans: [], shoppingLists: [], countSessions: [], hubStatusMap: {}, savingsPlanMap: {}, draftPoMap: {} });
  // user id → "First L." for records that stamp only an id (shopping lists,
  // counts). Resolved once per id for the whole board, not once per card.
  const [creatorNames, setCreatorNames] = useState({});
  const [shoppingListDetails, setShoppingListDetails] = useState({});

  // Modal / action states (kept for DraftOrderReview + lifecycle flows)
  const [draftReviewPo, setDraftReviewPo] = useState(null);
  const [actionStates, setActionStates] = useState({});
  const [vendorEmailModalPo, setVendorEmailModalPo] = useState(null);
  const [lifecycleModal, setLifecycleModal] = useState(null);
  const [followUpModal, setFollowUpModal] = useState(null);
  const [replacementConfirmPo, setReplacementConfirmPo] = useState(null);

  // Infinite scroll sentinel
  const sentinelRef = useRef(null);
  // Control bar ref — measures height to set --control-bar-height CSS var for chip row sticky offset
  const controlBarRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setCurrentUserId(data?.session?.user?.id ?? null);
    }).catch(() => {}).finally(() => {
      setAuthReady(true);
    });
    // Pre-populate search from URL param (?search=PO-1234) so deep-links from
    // dashboard alert cards land directly on the relevant purchase order.
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search') || urlParams.get('po');
    if (searchParam) {
      setSearchTerm(searchParam.trim());
      // Deep-linked PO search should land on History where that PO is visible
      setActiveTab('history');
    }
    // Preserve deep links to legacy views still supported but not primary nav.
    // 'savings-review' without a ?savings=<id> is the Purchase Planner's list
    // mode — every open plan — which is what a nav entry can link to, because
    // nav has no plan in hand the way a count hand-off or a queue card does.
    const legacyView = urlParams.get('view');
    if (legacyView && ['shopping-lists', 'vendors', 'counts-list', 'order-reporting', 'savings-review'].includes(legacyView)) {
      setView(legacyView);
    }
    const onResize = () => setIsMobile(window.innerWidth < 680);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Keep --control-bar-height CSS var in sync with the actual rendered height
  // so the chip row's sticky top offset is always exact.
  useEffect(() => {
    const el = controlBarRef.current;
    if (!el) return;
    const update = () => {
      document.documentElement.style.setProperty('--control-bar-height', `${el.offsetHeight}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reload when search/status/reload token changes — reset to page 0 (History tab only)
  useEffect(() => {
    if (view !== 'landing' || activeTab !== 'history') return;
    setAllPOs([]);
    setHubPage(0);
    setHubHasMore(true);
    setPosError('');
    setHasLoadedInitialPage(false);
  }, [debouncedSearch, hubStatusFilter, reloadToken, view, activeTab]);

  // Load POs when page changes or after reset (History tab only).
  // Guard on authReady so we never fire the initial fetch before Supabase has
  // resolved the session — on mobile WebView the session is read from
  // localStorage asynchronously and a premature query returns no rows (or
  // RLS-filtered rows) because the auth token isn't attached yet.
  useEffect(() => {
    if (view !== 'landing' || activeTab !== 'history') return;
    if (!storeId) return;
    if (!authReady) return;
    let cancelled = false;
    setPosLoading(true);
    listAllPOsForHub(storeId, {
      page: hubPage,
      pageSize: PAGE_SIZE,
      statusFilter: hubStatusFilter,
      search: debouncedSearch,
    })
      .then(({ data, hasMore }) => {
        if (cancelled) return;
        setAllPOs((prev) => hubPage === 0 ? data : [...prev, ...data]);
        setHubHasMore(hasMore);
      })
      .catch((err) => { if (!cancelled) setPosError(err.message || 'Failed to load orders.'); })
      .finally(() => {
        if (!cancelled) {
          setPosLoading(false);
          setHasLoadedInitialPage(true);
        }
      });
    return () => { cancelled = true; };
  }, [storeId, hubPage, hubStatusFilter, debouncedSearch, view, activeTab, authReady, reloadToken]);

  // Load problems (Needs Attention)
  const loadProblems = useCallback(async () => {
    if (!storeId) return;
    try {
      const p = await listOrderingProblems(storeId);
      setProblems(p || { staleDrafts: [], bouncedOrFailed: [], missingEmail: [], stalledPOs: [] });
    } catch { /* non-critical */ }
  }, [storeId]);

  useEffect(() => {
    if (view === 'landing' && authReady) loadProblems();
  }, [view, loadProblems, authReady, activeTab]);

  // Open savings reviews count (for badge compatibility + work queue)
  useEffect(() => {
    if (!authReady || !storeId || view !== 'landing') return;
    let cancelled = false;
    fetchOpenPlans(storeId)
      .then((plans) => { if (!cancelled) setOpenSavingsCount(plans.length); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [authReady, storeId, view]);

  // Purchasing work queue loader — Active tab only
  const loadWorkQueue = useCallback(async () => {
    if (!storeId || !authReady || view !== 'landing' || activeTab !== 'active') return;
    setQueueLoading(true);
    setQueueError('');
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
      const [problemsData, plansData, draftPOs, sentPOs, receivedPOs, openLists, completedLists, countRows] = await Promise.all([
        listOrderingProblems(storeId).catch(() => ({ staleDrafts: [], bouncedOrFailed: [], missingEmail: [], stalledPOs: [] })),
        fetchOpenPlans(storeId).catch(() => []),
        listDraftOrders(storeId).catch(() => []),
        listLatestSentPOs(storeId, { limit: 20 }).catch(() => []),
        listReceivedPOs(storeId, { includeArchived: false }).catch(() => []),
        fetchShoppingLists(storeId, { status: 'open' }).catch(() => []),
        fetchShoppingLists(storeId, { status: 'completed' }).catch(() => []),
        (async () => {
          try {
            let q = applyActiveFilter(
              supabase.from('count_sessions').select('id, name, created_at, completed_at, started_at, session_date, total_items, items_counted, created_by, notes, store_id, template_name_snapshot, template_type_snapshot').eq('store_id', storeId).eq('status', 'completed')
            ).gte('completed_at', thirtyDaysAgo).order('completed_at', { ascending: false }).limit(30);
            const { data, error } = await q;
            if (error) throw error;
            return data || [];
          } catch { return []; }
        })(),
      ]);

      // For completed counts, enrich with hub status, savings map, draft map
      const sessionIds = (countRows || []).map((s) => s.id);
      let hubStatusMap = {};
      let savingsPlanMap = {};
      let draftPoMap = {};
      if (sessionIds.length > 0) {
        try {
          const [hubMap, saveMap, draftMap] = await Promise.all([
            fetchSessionHubStatuses(sessionIds).catch(() => ({})),
            fetchOpenPlansForSessions(storeId, sessionIds).catch(() => ({})),
            // listDraftPOsForSessions is via orderingHubService but we inline fallback: use supabase query directly
            listDraftPOsForSessions(sessionIds, storeId).catch(() => ({})),
          ]);
          hubStatusMap = hubMap || {};
          savingsPlanMap = saveMap || {};
          draftPoMap = draftMap || {};
        } catch {}
      }

      // Shopping list details for open lists (to differentiate Ready vs In Progress)
      const shoppingListDetailsMap = {};
      // Fetch completed shopping lists details only for recent 5 to keep costs low; open lists get details for staging
      const listsForDetails = [...(openLists || []).slice(0, 10)];
      if (listsForDetails.length > 0) {
        const detailResults = await Promise.all(
          listsForDetails.map((list) =>
            fetchShoppingList(list.id)
              .then((detail) => ({ id: list.id, detail }))
              .catch(() => ({ id: list.id, detail: null }))
          )
        );
        detailResults.forEach(({ id, detail }) => {
          if (detail) shoppingListDetailsMap[String(id)] = detail;
        });
        setShoppingListDetails(shoppingListDetailsMap);
      } else {
        setShoppingListDetails({});
      }

      setWorkQueueRaw({ plans: plansData || [], shoppingLists: [...(openLists || []), ...(completedLists || []).slice(0, 10)], countSessions: countRows || [], hubStatusMap, savingsPlanMap, draftPoMap });

      // Build unified PO array for work queue: draft + sent + received (dedupe)
      const poCombinedMap = new Map();
      [...(draftPOs || []), ...(sentPOs || []), ...(receivedPOs || []), ...((problemsData?.staleDrafts) || []), ...((problemsData?.bouncedOrFailed) || []), ...((problemsData?.missingEmail) || []), ...((problemsData?.stalledPOs) || [])].forEach((po) => {
        if (po?.id && !poCombinedMap.has(String(po.id))) poCombinedMap.set(String(po.id), po);
      });
      const poCombined = Array.from(poCombinedMap.values());

      // Also include any POs already in problems but not in categorized lists
      const allShoppingForQueue = [...(openLists || []), ...(completedLists || []).slice(0, 10)];

      const queue = buildPurchasingWorkQueue({
        purchaseOrders: poCombined,
        shoppingLists: allShoppingForQueue,
        shoppingListDetails: shoppingListDetailsMap,
        countSessions: countRows || [],
        hubStatusMap,
        savingsPlanMap,
        draftPoMap,
        plans: plansData || [],
        problems: problemsData,
      });

      // Patch problems state so Needs Attention cards reuse same source
      setProblems(problemsData);
      setWorkQueue(queue);
    } catch (err) {
      setQueueError(err.message || 'Failed to load purchasing activity.');
    } finally {
      setQueueLoading(false);
    }
  }, [storeId, authReady, view, activeTab]);

  useEffect(() => {
    if (view === 'landing' && activeTab === 'active' && authReady && storeId) loadWorkQueue();
  }, [view, activeTab, authReady, storeId, loadWorkQueue]);

  // Put a name on every card. Records that already carry one (a PO's
  // ordered_by_name, a count's counted_by) are left alone; the rest are looked
  // up once per distinct user id and cached, so a board of twenty cards from
  // three people makes three requests.
  useEffect(() => {
    const ids = new Set();
    workQueue.activities.forEach((a) => {
      if (!a?.created_by_user_id) return;
      if (a.created_by_name) return;
      const key = String(a.created_by_user_id);
      if (!(key in creatorNames)) ids.add(key);
    });
    if (ids.size === 0) return undefined;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all([...ids].map(async (id) => {
        try { return [id, (await resolveUserProfileCompactName(id)) || '']; } catch { return [id, '']; }
      }));
      if (cancelled) return;
      setCreatorNames((prev) => {
        const next = { ...prev };
        entries.forEach(([id, name]) => { next[id] = name; });
        return next;
      });
    })();
    return () => { cancelled = true; };
    // creatorNames is read as a cache, not a trigger — including it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workQueue.activities]);

  const creatorNameFor = (activity) => (
    activity?.created_by_user_id ? (creatorNames[String(activity.created_by_user_id)] || '') : ''
  );

  // Infinite scroll via IntersectionObserver — History tab only
  useEffect(() => {
    if (view !== 'landing' || activeTab !== 'history') return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hubHasMore && !posLoading && hasLoadedInitialPage) {
          setHubPage((p) => p + 1);
        }
      },
      { rootMargin: '200px' }
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [hubHasMore, posLoading, hasLoadedInitialPage, view, activeTab]);

  const reloadList = useCallback(() => {
    setReloadToken((n) => n + 1);
    loadProblems();
    if (activeTab === 'active') loadWorkQueue();
  }, [loadProblems, activeTab, loadWorkQueue]);

  // Refresh the order list when Counter Intelligence edits a draft order from
  // the assistant drawer (it dispatches these on a successful apply), so a
  // changed quantity/total shows up here without a manual refresh — the same
  // wiring the Orders page uses. 'counter-intelligence:applied' fires for every
  // CI apply kind, so reload only for a purchase-order edit.
  useEffect(() => {
    const onChanged = (e) => {
      const kind = e?.detail?.kind;
      if (e?.type === 'counter-intelligence:applied' && kind && kind !== 'po_edit') return;
      reloadList();
    };
    window.addEventListener('purchaseOrdersChanged', onChanged);
    window.addEventListener('counter-intelligence:applied', onChanged);
    return () => {
      window.removeEventListener('purchaseOrdersChanged', onChanged);
      window.removeEventListener('counter-intelligence:applied', onChanged);
    };
  }, [reloadList]);

  // Sort control
  const handleSortClick = (key) => {
    if (key === sortKey) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'amount' ? 'desc' : 'asc');
    }
  };

  const visiblePOs = applySortClient(allPOs, sortKey, sortDir);

  const filteredBySearch = (activities, term) => {
    const t = String(term || '').trim().toLowerCase();
    if (!t) return activities || [];
    return (activities || []).filter((a) => {
      const hay = `${a.title || ''} ${a.subtitle || ''} ${a.vendor_name || ''} ${a.kind || ''}`.toLowerCase();
      return hay.includes(t);
    });
  };

  // Navigation handlers
  const handleSendOrdersClick = () => { setSelectedSessions([]); setReviewIndex(0); setView('counts-list'); };
  const handleSessionsSelected = (sessions) => { setSelectedSessions(sessions); setReviewIndex(0); setView('review'); };

  const handleActivityAction = useCallback((activity) => {
    if (!activity) return;
    const kind = activity.kind;
    const raw = activity.raw;
    if (kind === ACTIVITY_KIND.COUNT) {
      if (raw) handleSessionsSelected([raw]);
      else handleSendOrdersClick();
    } else if (kind === ACTIVITY_KIND.SAVINGS_REVIEW) {
      const planId = activity.purchasing_plan_id || raw?.id;
      if (planId) { setSavingsPlanId(planId); setView('savings-review'); }
    } else if (kind === ACTIVITY_KIND.PURCHASE_ORDER) {
      const po = raw;
      if (!po) return;
      const stage = activity.stage;
      if (stage === ACTIVITY_STAGE.READY || stage === ACTIVITY_STAGE.NEEDS_ATTENTION) {
        setDraftReviewPo(po);
      } else {
        setActiveTab('history');
        if (po.po_number) setSearchTerm(po.po_number);
      }
    } else if (kind === ACTIVITY_KIND.SHOPPING_LIST) {
      setView('shopping-lists');
    }
  }, []);
  const handleReviewNext = () => {
    if (reviewIndex < selectedSessions.length - 1) setReviewIndex((i) => i + 1);
    else setView('counts-list');
  };
  const handleReviewBack = () => { setView('counts-list'); setSelectedSessions([]); setReviewIndex(0); };

  const setPoActionState = (poId, state) => setActionStates((prev) => ({ ...prev, [poId]: state }));

  const handleDuplicate = async (po) => {
    setPoActionState(po.id, 'duplicating');
    try {
      await duplicatePurchaseOrderAsDraft(po.id);
      reloadList();
    } catch (err) { alert(`Failed to duplicate: ${err.message}`); }
    setPoActionState(po.id, null);
  };

  const handleArchive = async (po) => {
    setPoActionState(po.id, 'archiving');
    try {
      await archivePurchaseOrder(po.id);
      reloadList();
    } catch (err) { alert(`Failed to archive: ${err.message}`); }
    setPoActionState(po.id, null);
  };

  const handleCreateReplacement = async (po) => {
    setPoActionState(po.id, 'replacing');
    try {
      const result = await createReplacementDraft(po.id);
      reloadList();
      setFollowUpModal(null);
      if (result?.draft_po_id) {
        const { data: draftRow } = await supabase
          .from('purchase_orders')
          .select('*')
          .eq('id', result.draft_po_id)
          .maybeSingle();
        if (draftRow) setDraftReviewPo(draftRow);
      }
    } catch (err) { alert(`Failed to create replacement: ${err.message}`); }
    setPoActionState(po.id, null);
  };

  const handleOpenLinkedPo = (poId, poNumber) => {
    setActiveTab('history');
    if (poNumber) {
      setSearchTerm(poNumber);
      setHubStatusFilter('all');
    } else if (poId) {
      const el = document.querySelector(`[data-po-id="${poId}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
      setHubStatusFilter('all');
    }
  };

  const openLifecycleModal = (type, po) => setLifecycleModal({ type, po, busy: false, error: '', reason: '' });
  const closeLifecycleModal = () => setLifecycleModal(null);

  const commitLifecycleAction = async () => {
    const { type, po, reason } = lifecycleModal;
    setLifecycleModal((prev) => ({ ...prev, busy: true, error: '' }));
    try {
      if (type === 'delete_draft') {
        await deleteDraftPurchaseOrder(po.id);
        setLifecycleModal(null);
        reloadList();
      } else if (type === 'cancel_po') {
        await cancelPurchaseOrder(po.id, { reason: reason || null });
        setLifecycleModal(null);
        reloadList();
        if (FOLLOW_UP_TRIGGERS.has(reason)) setFollowUpModal({ po });
      } else if (type === 'create_replacement') {
        await handleCreateReplacement(po);
        setLifecycleModal(null);
      } else if (type === 'void_po') {
        await voidPurchaseOrder(po.id, { reason: reason || null });
        setLifecycleModal(null);
        reloadList();
      }
    } catch (err) {
      setLifecycleModal((prev) => ({ ...prev, busy: false, error: err.message || 'Action failed.' }));
    }
  };

  const handleDraftSent = useCallback(async (_result) => {
    setDraftReviewPo(null);
    reloadList();
  }, [reloadList]);

  const handleDraftDeleted = async () => { setDraftReviewPo(null); reloadList(); };

  const handleVendorEmailSaved = useCallback(async (_vendorData, resolvedVendorId) => {
    const po = vendorEmailModalPo;
    setVendorEmailModalPo(null);
    if (resolvedVendorId && po && !po.vendor_id) {
      try {
        await updateDraftPurchaseOrder(po.id, { vendor_id: resolvedVendorId });
      } catch (err) {
        console.warn('[OrderingHub] Could not persist resolved vendor_id:', err.message);
      }
    }
    reloadList();
  }, [vendorEmailModalPo, reloadList]);

  const attentionItems = [
    ...problems.bouncedOrFailed.map((p) => ({ ...p, _problemType: 'failed' })),
    ...problems.missingEmail.map((p) => ({ ...p, _problemType: 'no_email' })),
    ...problems.staleDrafts.map((p) => ({ ...p, _problemType: 'stale_draft' })),
    ...(problems.stalledPOs || [])
      .filter((p) => businessHoursElapsed(p.sent_at) >= 24)
      .map((p) => ({ ...p, _problemType: 'stalled' })),
  ];
  const problemCount = attentionItems.length;

  // ---- Non-landing views ----

  if (view === 'savings-review') {
    return (
      <div>
        <style>{mobileCss}</style>
        <div style={backBarStyle} className="ohub-back-bar">
          <button
            type="button"
            style={backBtnStyle}
            onClick={() => {
              // From a worksheet, back means the review list; from the list,
              // back means the Ordering landing.
              if (savingsPlanId) setSavingsPlanId(null);
              else setView('landing');
            }}
          >
            ← {savingsPlanId ? 'All purchase plans' : 'Back to Ordering'}
          </button>
          <span style={backCrumbStyle}>Purchase Planner</span>
        </div>
        <SavingsReviewContainer
          storeId={storeId}
          planId={savingsPlanId}
          onOpenPlan={(id) => setSavingsPlanId(id)}
          onBack={() => { if (savingsPlanId) setSavingsPlanId(null); else setView('landing'); }}
          onViewOrders={() => { setSavingsPlanId(null); setView('order-reporting'); }}
        />
      </div>
    );
  }

  if (view === 'order-reporting') {
    return (
      <div>
        <style>{mobileCss}</style>
        <div style={backBarStyle} className="ohub-back-bar">
          <button type="button" style={backBtnStyle} onClick={() => setView('landing')}>← Back to Ordering</button>
          <span style={backCrumbStyle}>Order Reporting</span>
        </div>
        <OrdersPage storeId={storeId} storeName={storeName} storeAddress={storeAddress} userEmail={userEmail} userName={userName} />
      </div>
    );
  }

  if (view === 'shopping-lists') {
    return (
      <div>
        <style>{mobileCss}</style>
        <div style={backBarStyle} className="ohub-back-bar">
          <button type="button" style={backBtnStyle} onClick={() => setView('landing')}>← Back to Ordering</button>
          <span style={backCrumbStyle}>Shopping List Editor</span>
        </div>
        <div style={{ padding: '20px 0 32px' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto 16px' }}>
            <h1 style={{ ...titleStyle, fontSize: 26, marginBottom: 4 }}>Shopping Lists</h1>
            <div style={subtitleStyle}>Open a vendor trip to edit the planned list or record what was actually bought.</div>
          </div>
          <ShoppingListView
            storeId={storeId}
            userId={currentUserId}
            onEditVendor={(vendorId) => {
              setVendorEditorTargetId(vendorId);
              setVendorEditorReturnView('shopping-lists');
              setView('vendors');
            }}
          />
        </div>
      </div>
    );
  }

  if (view === 'vendors') {
    return (
      <div>
        <style>{mobileCss}</style>
        <div style={backBarStyle} className="ohub-back-bar">
          <button
            type="button"
            style={backBtnStyle}
            onClick={() => {
              setVendorEditorTargetId(null);
              setView(vendorEditorReturnView);
            }}
          >
            ← {vendorEditorReturnView === 'shopping-lists' ? 'Back to Shopping Lists' : 'Back to Ordering'}
          </button>
          <span style={backCrumbStyle}>Vendors</span>
        </div>
        <VendorEditor initialVendorId={vendorEditorTargetId} />
      </div>
    );
  }

  if (view === 'counts-list') {
    return (
      <div>
        <style>{mobileCss}</style>
        {draftReviewPo && (
          <DraftOrderReview
            po={draftReviewPo}
            storeId={storeId}
            onClose={() => setDraftReviewPo(null)}
            onSent={handleDraftSent}
            onDeleted={handleDraftDeleted}
            onSaved={(updatedPo) => setDraftReviewPo((prev) => prev ? { ...prev, ...updatedPo } : prev)}
          />
        )}
        <div style={backBarStyle} className="ohub-back-bar">
          <button type="button" style={backBtnStyle} onClick={() => setView('landing')}>← Back to Ordering</button>
          <span style={backCrumbStyle}>Send Orders / Generate PO</span>
        </div>
        <CompletedCountsList
          storeId={storeId}
          currentUserId={currentUserId}
          onReview={handleSessionsSelected}
          onOpenDraft={(po) => setDraftReviewPo(po)}
          onOpenSavingsReview={(planId) => { setSavingsPlanId(planId); setView('savings-review'); }}
        />
      </div>
    );
  }

  if (view === 'review' && selectedSessions.length > 0) {
    const session = selectedSessions[reviewIndex];
    return (
      <div>
        <style>{mobileCss}</style>
        {draftReviewPo && (
          <DraftOrderReview
            po={draftReviewPo}
            storeId={storeId}
            onClose={() => setDraftReviewPo(null)}
            onSent={handleDraftSent}
            onDeleted={handleDraftDeleted}
            onSaved={(updatedPo) => setDraftReviewPo((prev) => prev ? { ...prev, ...updatedPo } : prev)}
          />
        )}
        <div style={backBarStyle} className="ohub-back-bar">
          <button type="button" style={backBtnStyle} onClick={handleReviewBack}>← Back to Counts List</button>
          <span style={backCrumbStyle}>
            Review Order{selectedSessions.length > 1 && ` (${reviewIndex + 1} of ${selectedSessions.length})`}
          </span>
        </div>
        <CountOrderReview
          key={session.id}
          session={session}
          storeId={storeId}
          storeName={storeName}
          storeAddress={storeAddress}
          userEmail={userEmail}
          userName={userName}
          isMulti={selectedSessions.length > 1}
          reviewIndex={reviewIndex}
          reviewTotal={selectedSessions.length}
          onNext={handleReviewNext}
          onCancel={handleReviewBack}
          onOpenDraft={(po) => setDraftReviewPo(po)}
        />
      </div>
    );
  }

  // ---- Landing view ----

  return (
    <div style={shellStyle} className="ohub-shell">
      <style>{mobileCss}</style>

      {draftReviewPo && (
        <DraftOrderReview
          po={draftReviewPo}
          storeId={storeId}
          onClose={() => setDraftReviewPo(null)}
          onSent={handleDraftSent}
          onDeleted={handleDraftDeleted}
          onSaved={(updatedPo) => setDraftReviewPo((prev) => prev ? { ...prev, ...updatedPo } : prev)}
        />
      )}

      {/* This card said "Add Email", so the sheet opens in the email box
          rather than at the top of the vendor's settings. */}
      {vendorEmailModalPo && (
        <VendorContactModal
          vendorId={vendorEmailModalPo.vendor_id || null}
          vendorName={vendorEmailModalPo.vendor_name_snapshot || null}
          storeId={vendorEmailModalPo.store_id || storeId || null}
          focusField="contact_email"
          onClose={() => setVendorEmailModalPo(null)}
          onSaved={handleVendorEmailSaved}
        />
      )}

      {lifecycleModal && (
        <LifecycleModal
          modal={lifecycleModal}
          onClose={closeLifecycleModal}
          onConfirm={commitLifecycleAction}
          onReasonChange={(r) => setLifecycleModal((prev) => ({ ...prev, reason: r }))}
        />
      )}

      {followUpModal && (
        <FollowUpModal
          po={followUpModal.po}
          onClose={() => setFollowUpModal(null)}
          onCreateReplacement={(po) => { setFollowUpModal(null); handleCreateReplacement(po); }}
          onReschedule={async (po) => {
            setFollowUpModal(null);
            try {
              const result = await createReplacementDraft(po.id);
              reloadList();
              if (result?.draft_po_id) {
                const { data: draftRow } = await supabase
                  .from('purchase_orders')
                  .select('*')
                  .eq('id', result.draft_po_id)
                  .maybeSingle();
                if (draftRow) setDraftReviewPo(draftRow);
              }
            } catch (err) { alert(`Failed to create reschedule draft: ${err.message}`); }
          }}
        />
      )}

      <CustomModal
        isOpen={!!replacementConfirmPo}
        onClose={() => setReplacementConfirmPo(null)}
        type="confirm"
        title="Create replacement PO?"
        message="Creates a new editable draft copied from this PO. The original will be marked as replaced once the replacement is sent."
        confirmText="Create Replacement"
        cancelText="Cancel"
        confirmButtonStyle="primary"
        onConfirm={() => { handleCreateReplacement(replacementConfirmPo); setReplacementConfirmPo(null); }}
      />

      {/* ---- Purchasing header ---- */}
      <div style={purchasingHeaderStyle} className="ohub-purchasing-header">
        <div style={purchasingTitleBlock}>
          <h1 style={purchasingTitleStyle} className="ohub-title">Purchasing</h1>
          <div style={purchasingSubtitleStyle} className="ohub-subtitle">
            See what needs buying, send orders, shop pickup lists, and track completed purchases.
          </div>
          {storeName && <div style={storeNameStyle}>{storeName}</div>}
        </div>
      </div>

      <nav style={purchasingDomainNav} className="ohub-domain-nav" aria-label="Purchasing destinations">
        <a href="/ordering.html?view=savings-review" style={purchasingDomainLink} className="ohub-domain-link">
          <span style={purchasingDomainIcon} aria-hidden="true">⚖️</span>
          <span><strong style={purchasingDomainTitle}>Purchase Planner</strong><small style={purchasingDomainDescription}>Compare vendors and arrange the buy.</small></span>
        </a>
        <a href="/ordering.html" style={purchasingDomainLinkActive} className="ohub-domain-link is-active" aria-current="page">
          <span style={purchasingDomainIcon} aria-hidden="true">🧾</span>
          <span><strong style={purchasingDomainTitle}>Orders</strong><small style={purchasingDomainDescription}>Plan and manage active purchases.</small></span>
        </a>
        <a href="/orders.html" style={purchasingDomainLink} className="ohub-domain-link">
          <span style={purchasingDomainIcon} aria-hidden="true">📨</span>
          <span><strong style={purchasingDomainTitle}>Purchase Orders</strong><small style={purchasingDomainDescription}>Search, review, and resend POs.</small></span>
        </a>
        <a href="/vendor-editor.html" style={purchasingDomainLink} className="ohub-domain-link">
          <span style={purchasingDomainIcon} aria-hidden="true">🚚</span>
          <span><strong style={purchasingDomainTitle}>Vendors</strong><small style={purchasingDomainDescription}>Manage purchasing sources.</small></span>
        </a>
        <a href="/invoice-inbox.html" style={purchasingDomainLink} className="ohub-domain-link">
          <span style={purchasingDomainIcon} aria-hidden="true">📬</span>
          <span><strong style={purchasingDomainTitle}>Invoices</strong><small style={purchasingDomainDescription}>Review and import purchase records.</small></span>
        </a>
      </nav>

      {/* ---- Active / History tabs ---- */}
      <div style={purchasingTabBar} className="ohub-purchasing-tabs" role="tablist" aria-label="Purchasing views">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'active'}
          aria-controls="purchasing-active-panel"
          data-testid="purchasing-tab-active"
          style={purchasingTabBtn(activeTab === 'active')}
          onClick={() => setActiveTab('active')}
        >
          Active
          {(workQueue.needsAttention.length + workQueue.ready.length + workQueue.inProgress.length) > 0 && (
            <span style={tabCountBadge(activeTab === 'active')}>{workQueue.needsAttention.length + workQueue.ready.length + workQueue.inProgress.length}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'history'}
          aria-controls="purchasing-history-panel"
          data-testid="purchasing-tab-history"
          style={purchasingTabBtn(activeTab === 'history')}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      {/* =========================================================== */}
      {/* Active Purchasing — lifecycle stages */}
      {/* =========================================================== */}
      {activeTab === 'active' && (
        <div id="purchasing-active-panel" role="tabpanel" style={activePanelStyle}>
          {queueError && (
            <div style={errorStyle}>
              {queueError}
              <button style={retryBtn} onClick={() => loadWorkQueue()}>Retry</button>
            </div>
          )}

          {queueLoading && workQueue.activities.length === 0 && (
            <div style={loadingStyle}>Loading purchasing activity…</div>
          )}

          {/* Small active-work search — filters visible cards client-side */}
          <div style={activeSearchWrap}>
            <span style={searchIconStyle}>🔍</span>
            <input
              type="text"
              value={activeSearch}
              onChange={(e) => setActiveSearch(e.target.value)}
              placeholder="Search active purchases…"
              style={activeSearchInput}
              aria-label="Search active purchases"
            />
            {activeSearch && <button style={searchClearBtn} onClick={() => setActiveSearch('')} aria-label="Clear active search">×</button>}
          </div>

          {/* Stage: Needs attention */}
          <StageSection
            title="Needs attention"
            count={filteredBySearch(workQueue.needsAttention, activeSearch).length}
            testId="stage-needs-attention"
            emptyText={null}
          >
            {filteredBySearch(workQueue.needsAttention, activeSearch).map((activity) => (
              <PurchasingActivityCard
                key={activity.id}
                activity={activity}
                resolvedCreatorName={creatorNameFor(activity)}
                onAction={() => handleActivityAction(activity)}
                onOpenDraft={(po) => setDraftReviewPo(po)}
                onAddEmail={(po) => setVendorEmailModalPo(po)}
                onOpenLifecycleModal={openLifecycleModal}
                onCreateReplacement={(po) => setReplacementConfirmPo(po)}
                onDuplicate={handleDuplicate}
                onArchive={handleArchive}
                actionState={activity.purchase_order_id ? actionStates[activity.purchase_order_id] : null}
              />
            ))}
            {/* Legacy attention items that are POs not yet in the unified queue (before queue loads) */}
            {!queueLoading && workQueue.needsAttention.length === 0 && problemCount > 0 && (
              <div style={legacyAttentionNote}>
                {attentionItems.map((po) => (
                  <ProblemRow
                    key={`${po._problemType}-${po.id}`}
                    po={po}
                    actionState={actionStates[po.id]}
                    onOpenDraft={() => setDraftReviewPo(po)}
                    onOpenLifecycleModal={openLifecycleModal}
                    onCreateReplacement={(po) => setReplacementConfirmPo(po)}
                    onDuplicate={handleDuplicate}
                    onArchive={handleArchive}
                    onAddEmail={() => setVendorEmailModalPo(po)}
                  />
                ))}
              </div>
            )}
          </StageSection>

          {/* Stage: Ready to buy */}
          <StageSection
            title="Ready to buy"
            count={filteredBySearch(workQueue.ready, activeSearch).length}
            testId="stage-ready-to-buy"
            emptyText={null}
          >
            {filteredBySearch(workQueue.ready, activeSearch).map((activity) => (
              <PurchasingActivityCard
                key={activity.id}
                activity={activity}
                resolvedCreatorName={creatorNameFor(activity)}
                onAction={() => handleActivityAction(activity)}
                onOpenDraft={(po) => setDraftReviewPo(po)}
                onOpenPlan={(id) => { setSavingsPlanId(id); setView('savings-review'); }}
                onReviewCount={(session) => handleSessionsSelected([session])}
                onOpenShoppingList={(id) => setView('shopping-lists')}
              />
            ))}
          </StageSection>

          {/* Stage: In progress */}
          <StageSection
            title="In progress"
            count={filteredBySearch(workQueue.inProgress, activeSearch).length}
            testId="stage-in-progress"
            emptyText={null}
          >
            {filteredBySearch(workQueue.inProgress, activeSearch).map((activity) => (
              <PurchasingActivityCard
                key={activity.id}
                activity={activity}
                resolvedCreatorName={creatorNameFor(activity)}
                onAction={() => handleActivityAction(activity)}
                onOpenShoppingList={(id) => setView('shopping-lists')}
              />
            ))}
          </StageSection>

          {/* Recent purchases */}
          <div style={recentHeaderWrap}>
            <h3 style={stageTitleStyle}>Recent purchases</h3>
            {workQueue.recent.length > 0 && <span style={stageCountStyle}>{workQueue.recent.length}</span>}
          </div>
          <div style={stageCardsWrap} data-testid="stage-recent-purchases">
            {filteredBySearch(workQueue.recent, activeSearch).length === 0 && !queueLoading ? (
              <div style={stageEmptyStyle}>{workQueue.activities.length === 0 && !queueLoading ? 'No completed purchases yet.' : 'No recent purchases match.'}</div>
            ) : (
              filteredBySearch(workQueue.recent, activeSearch).map((activity) => (
                <PurchasingActivityCard
                  key={`recent-${activity.id}`}
                  activity={activity}
                  resolvedCreatorName={creatorNameFor(activity)}
                  onAction={() => handleActivityAction(activity)}
                  isRecent
                />
              ))
            )}
          </div>
          <div style={viewHistoryLinkWrap}>
            <button style={viewHistoryLink} onClick={() => setActiveTab('history')} data-testid="view-purchase-history">
              View purchase history →
            </button>
          </div>

          {/* Global empty state — You're caught up */}
          {!queueLoading && !queueError && workQueue.needsAttention.length === 0 && workQueue.ready.length === 0 && workQueue.inProgress.length === 0 && (
            <div style={caughtUpCard} data-testid="caught-up-state">
              <div style={caughtUpTitle}>You're caught up.</div>
              <div style={caughtUpSub}>No purchases need action right now.</div>
              {workQueue.recent.length > 0 && <div style={caughtUpHint}>Recent purchases are shown above. View history for everything.</div>}
            </div>
          )}

          {/* Secondary explicit action — Review completed counts */}
          <div style={secondaryActionsWrap}>
            <button style={secondaryLinkBtn} onClick={handleSendOrdersClick} data-testid="review-completed-counts-link">
              Review completed counts
            </button>
            <span style={secondaryHint}>or browse all counts needing review</span>
          </div>
        </div>
      )}

      {/* =========================================================== */}
      {/* History — deep historical view (authoritative: OrdersPage) */}
      {/* =========================================================== */}
      {activeTab === 'history' && (
        <div id="purchasing-history-panel" role="tabpanel" style={historyPanelStyle}>
          <OrdersPage
            storeId={storeId}
            storeName={storeName}
            storeAddress={storeAddress}
            userEmail={userEmail}
            userName={userName}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Purchasing workspace — stage & card components
// ---------------------------------------------------------------------------

function StageSection({ title, count, children, testId, emptyText }) {
  const hasChildren = Array.isArray(children) ? children.some(Boolean) && children.flat().filter(Boolean).length > 0 : !!children;
  // When count is 0 and no explicit emptyText, hide the empty note — the global "You're caught up" covers total emptiness
  // But for stages like Ready/In progress we want to show count even when zero only if there is other context; simpler render header always
  return (
    <div style={stageSectionWrap} data-testid={testId}>
      <div style={stageHeaderRow}>
        <h3 style={stageTitleStyle}>{title}</h3>
        <span style={stageCountStyle}>{count}</span>
      </div>
      <div style={stageCardsWrap}>
        {hasChildren ? children : (emptyText ? <div style={stageEmptyStyle}>{emptyText}</div> : null)}
        {!hasChildren && !emptyText && count === 0 ? <div style={stageEmptyMuted}>Nothing here right now.</div> : null}
      </div>
    </div>
  );
}

export function PurchasingActivityCard({ activity, onAction, isRecent, onOpenDraft, onOpenPlan, onReviewCount, onOpenShoppingList, onOpenLifecycleModal, onCreateReplacement, onDuplicate, onArchive, onAddEmail, actionState, resolvedCreatorName = '' }) {
  if (!activity) return null;
  const kind = activity.kind;
  const stage = activity.stage;
  const badge = kindBadge(activity.kind, activity.stage);
  const amountText = activity.amount ? amountLabel(activity.amount, activity.amount_kind) : null;
  const itemText = activity.item_count ? `${activity.item_count} ${activity.item_count === 1 ? 'item' : 'items'}` : null;
  const title = activity.title || activity.vendor_name || 'Purchase';
  const subtitle = activity.subtitle || '';
  // Who started this and when, in the reader's own clock. A draft nobody
  // recognises is a draft nobody dares send.
  const createdWho = activity.created_by_name || resolvedCreatorName || '';
  const createdWhen = activity.created_at ? formatLocalDateTime(activity.created_at) : '';
  const createdMeta = createdWho || createdWhen
    ? `Created${createdWho ? ` by ${createdWho}` : ''}${createdWhen ? ` · ${createdWhen}` : ''}`
    : '';

  // Attention explanatory text
  const attentionWhat = activity.problem_type ? attentionExplain(activity).what : null;
  const attentionWhy = activity.problem_type ? attentionExplain(activity).why : null;

  // Decide primary action label/handler
  const primaryLabel = activity.next_action?.label || 'View';
  const handlePrimary = () => {
    // If card provides a specific handler via props, use it
    if (kind === ACTIVITY_KIND.PURCHASE_ORDER && activity.raw) {
      if (stage === ACTIVITY_STAGE.NEEDS_ATTENTION) {
        if (activity.problem_type === 'missing_email' && onAddEmail) { onAddEmail(activity.raw); return; }
        if (activity.problem_type === 'stale_draft' && onOpenDraft) { onOpenDraft(activity.raw); return; }
      }
      if (stage === ACTIVITY_STAGE.READY && onOpenDraft) { onOpenDraft(activity.raw); return; }
    }
    if (kind === ACTIVITY_KIND.SAVINGS_REVIEW && onOpenPlan) { onOpenPlan(activity.purchasing_plan_id); return; }
    if (kind === ACTIVITY_KIND.COUNT && onReviewCount && activity.raw) { onReviewCount(activity.raw); return; }
    if (kind === ACTIVITY_KIND.SHOPPING_LIST && onOpenShoppingList) { onOpenShoppingList(activity.shopping_list_id); return; }
    if (onAction) onAction();
  };

  return (
    <div style={activityCardStyle(stage)} className="purchasing-activity-card" data-testid={`activity-card-${activity.id}`}>
      <div style={activityTopRow}>
        <div style={activityVendorBlock}>
          <div style={activityTitle}>{title}</div>
          {subtitle && <div style={activitySubtitle}>{subtitle}</div>}
          {attentionWhat && (
            <div style={attentionExplainBox}>
              <div style={attentionExplainWhat}>{attentionWhat}</div>
              <div style={attentionExplainWhy}>{attentionWhy}</div>
            </div>
          )}
          {badge && (
            <div style={activityBadgeRow}>
              <span style={badgeStyle(badge.bg, badge.fg)}>{badge.label}</span>
            </div>
          )}
          {/* Facts as pills, at fixed widths and always in the same order, so
              running a thumb down the list reads one column of counts and one
              column of money instead of a different sentence on every card. */}
          <div style={activityMetaRow} data-testid={`activity-facts-${activity.id}`}>
            <span style={factPill(PILL_COUNT, 84)}>{itemText || '—'}</span>
            <span style={factPill(amountPillColors(activity), 108)}>{amountText || '—'}</span>
          </div>
          {createdMeta && <div style={activityCreatedMeta}>{createdMeta}</div>}
        </div>
        <div style={activityActionBlock}>
          <button
            type="button"
            style={activityPrimaryBtn(stage)}
            onClick={handlePrimary}
            data-testid={`activity-action-${activity.id}`}
          >
            {primaryLabel} →
          </button>
        </div>
      </div>
      {isRecent && activity.updated_at && (
        <div style={activityFooter}>{activityFooterText(activity)}</div>
      )}
    </div>
  );
}

function kindBadge(kind, stage) {
  if (stage === ACTIVITY_STAGE.NEEDS_ATTENTION) return { label: 'Needs attention', bg: '#FDECEA', fg: '#b71c1c' };
  const map = {
    [ACTIVITY_KIND.PURCHASE_ORDER]: { label: 'PO', bg: '#E8F5E9', fg: '#2E7D32' },
    [ACTIVITY_KIND.SHOPPING_LIST]: { label: 'Shopping trip', bg: '#E3F2FD', fg: '#0d47a1' },
    [ACTIVITY_KIND.COUNT]: { label: 'Count', bg: '#FFF4E0', fg: '#8A5A00' },
    [ACTIVITY_KIND.SAVINGS_REVIEW]: { label: 'Purchase plan', bg: '#F3E5F5', fg: '#6A1B9A' },
  };
  return map[kind] || { label: kind, bg: '#F5F5F5', fg: '#5A4A42' };
}

function attentionExplain(activity) {
  const pt = activity.problem_type;
  if (pt === 'stale_draft') return { what: 'Stale draft', why: 'This draft has been waiting over 48 hours and may be forgotten. → why it matters → what to do: review the draft' };
  if (pt === 'missing_email') return { what: 'Missing vendor email', why: 'No email on file, so the order cannot be sent. Add the vendor email to send.' };
  if (pt === 'bounced_failed') return { what: 'Delivery failed', why: 'The vendor email bounced or failed — they did not receive the order. Fix the email and resend.' };
  if (pt === 'stalled') return { what: 'Not opened by vendor', why: 'Sent but not opened in 24 business hours — vendor may not have seen it. Follow up.' };
  return { what: 'Needs attention', why: 'This purchase needs review.' };
}

function activityFooterText(activity) {
  try {
    const d = new Date(activity.updated_at);
    return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} · ${activityFooterLabel(activity)}`;
  } catch { return activityFooterLabel(activity); }
}
function activityFooterLabel(activity) {
  if (activity.kind === ACTIVITY_KIND.PURCHASE_ORDER) {
    if (activity.stage === ACTIVITY_STAGE.COMPLETED) return 'Purchase completed';
    return 'Purchase';
  }
  if (activity.kind === ACTIVITY_KIND.SHOPPING_LIST) return 'Shopping trip completed';
  return 'Completed';
}

// ---------------------------------------------------------------------------
// Lifecycle modals
// ---------------------------------------------------------------------------

function LifecycleModal({ modal, onClose, onConfirm, onReasonChange }) {
  const { type, po, busy, error, reason } = modal;
  const vendorName = po?.vendor_name_snapshot || 'this vendor';

  const configs = {
    delete_draft: {
      title: 'Delete draft PO?',
      body: 'This purchase order has not been sent. Deleting it will permanently remove the draft.',
      confirmText: 'Delete Draft',
      cancelText: 'Cancel',
      confirmColor: '#b71c1c',
      reasons: null,
    },
    cancel_po: {
      title: 'Cancel this PO?',
      body: 'This purchase order has already been sent. Canceling it will preserve the original order in history and mark it as canceled.',
      confirmText: 'Cancel PO',
      cancelText: 'Keep PO',
      confirmColor: '#b71c1c',
      reasons: CANCEL_REASONS,
      reasonLabel: 'Reason (optional)',
    },
    create_replacement: {
      title: 'Create replacement PO?',
      body: 'This will keep the original PO in history and create a new editable copy. The original PO will be marked as replaced once the replacement is sent.',
      confirmText: 'Create Replacement',
      cancelText: 'Cancel',
      confirmColor: '#5A4A42',
      reasons: null,
    },
    void_po: {
      title: 'Void this PO?',
      body: 'This PO is part of your purchase history. Voiding keeps the record for audit purposes but excludes it from active purchase totals and reports by default.',
      confirmText: 'Void PO',
      cancelText: 'Cancel',
      confirmColor: '#b71c1c',
      reasons: VOID_REASONS,
      reasonLabel: 'Reason (optional)',
    },
  };

  const cfg = configs[type] || configs.delete_draft;

  return (
    <CustomModal
      isOpen={true}
      onClose={() => { if (!busy) onClose(); }}
      type="custom"
      title={cfg.title}
      message={po ? `${vendorName}${po.po_number ? ` · ${po.po_number}` : ''}` : undefined}
      messageAlign="left"
    >
      <p style={{ fontSize: 13, color: '#5A4A42', lineHeight: 1.5, margin: '0 0 16px' }}>{cfg.body}</p>
      {cfg.reasons && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#7b6b61', marginBottom: 6, textTransform: 'uppercase' }}>{cfg.reasonLabel}</div>
          <select
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #d5c9c0', fontSize: 13, color: '#2b1d14', background: '#fff', fontFamily: 'inherit' }}
            disabled={busy}
          >
            {cfg.reasons.map((r) => <option key={r} value={r}>{r || '— No reason —'}</option>)}
          </select>
        </div>
      )}
      {error && <div style={{ background: '#fde8e8', border: '1.5px solid #ef9a9a', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#8b0000', marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <button style={modalCancelBtn} onClick={onClose} disabled={busy}>{cfg.cancelText}</button>
        <button
          style={{ ...modalConfirmBtn, background: cfg.confirmColor, borderColor: cfg.confirmColor, opacity: busy ? 0.6 : 1 }}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Working…' : cfg.confirmText}
        </button>
      </div>
    </CustomModal>
  );
}

function FollowUpModal({ po, onClose, onCreateReplacement, onReschedule }) {
  return (
    <div style={modalOverlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalBoxStyle}>
        <div style={{ fontWeight: 800, fontSize: 17, color: '#2b1d14', marginBottom: 6 }}>What would you like to do next?</div>
        <p style={{ fontSize: 13, color: '#5A4A42', lineHeight: 1.5, margin: '0 0 18px' }}>
          The PO has been canceled. You can create a replacement order, reschedule, or close without further action.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button style={{ ...modalConfirmBtn, background: '#5A4A42', borderColor: '#5A4A42', textAlign: 'left' }} onClick={() => onCreateReplacement(po)}>
            Create Replacement Order
          </button>
          <button style={{ ...modalConfirmBtn, background: '#7b6b61', borderColor: '#7b6b61', textAlign: 'left' }} onClick={() => onReschedule(po)}>
            Reschedule Order
          </button>
          <button style={modalCancelBtn} onClick={onClose}>Close Without Replacement</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function EmptyState({ children }) {
  return <div style={{ color: '#7b6b61', fontSize: 13, padding: '16px 0', textAlign: 'center' }}>{children}</div>;
}

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || { bg: '#F5F5F5', fg: '#5A4A42', label: status || '—' };
  return (
    <span style={{ background: s.bg, color: s.fg, borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

const bannerLinkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit', fontFamily: 'inherit', lineHeight: 'inherit', color: 'inherit', fontWeight: 700 };

function ProblemRow({ po, actionState, onOpenDraft, onOpenLifecycleModal, onCreateReplacement, onDuplicate, onArchive, onAddEmail }) {
  const isDraft = po.delivery_status === 'draft';
  const isStale = po._problemType === 'stale_draft';
  const isBounced = po._problemType === 'failed';
  const isNoEmail = po._problemType === 'no_email';
  const isStalled = po._problemType === 'stalled';
  const bhElapsed = isStalled ? Math.floor(businessHoursElapsed(po.sent_at)) : 0;
  const problemLabel = isStale
    ? `Stale draft (${fmtAgo(po.created_at)})`
    : isBounced
      ? `${STATUS_STYLES[po.delivery_status]?.label || po.delivery_status} — delivery issue`
      : isStalled
        ? `Not opened · sent ${fmtAgo(po.sent_at)} (${bhElapsed}h biz)`
        : 'No vendor email on file';
  return (
    <div style={problemRowStyle}>
      <div style={rowMain}>
        <div style={rowTopLine}>
          <span style={problemChip}>{problemLabel}</span>
          <span style={vendorNameStyle}>{po.vendor_name_snapshot || 'Unknown Vendor'}</span>
          <StatusPill status={derivePoStatus(po)} />
        </div>
        <div style={rowMeta}>
          <span>{po.po_number}</span>
          <span>·</span>
          <span>{fmtDate(po.sent_at || po.created_at)}</span>
          {po.total > 0 && <><span>·</span><span>{fmt(po.total)}</span></>}
        </div>
      </div>
      <div style={rowActions} className="ohub-problem-actions">
        {isDraft && <button style={actionBtnPrimary} onClick={onOpenDraft}>Review Draft</button>}
        {!isDraft && isNoEmail && (
          <button style={actionBtnPrimary} onClick={() => onAddEmail && onAddEmail(po)}>Add Email →</button>
        )}
        {!isDraft && (
          <button style={actionBtnSecondary} onClick={() => onOpenLifecycleModal && onOpenLifecycleModal('cancel_po', po)} disabled={actionState === 'canceling'}>
            Cancel PO
          </button>
        )}
        {!isDraft && !po.replaced_by_po_id && (
          <button style={actionBtnSecondary} onClick={() => onCreateReplacement && onCreateReplacement(po)} disabled={actionState === 'replacing'}>
            {actionState === 'replacing' ? 'Creating…' : 'Create Replacement'}
          </button>
        )}
        {!isDraft && (
          <button style={actionBtnSecondary} onClick={() => onDuplicate && onDuplicate(po)} disabled={actionState === 'duplicating'}>
            {actionState === 'duplicating' ? 'Copying…' : 'Duplicate'}
          </button>
        )}
        {!isDraft && (
          <button style={actionBtnSecondary} onClick={() => onArchive && onArchive(po)} disabled={actionState === 'archiving'}>
            {actionState === 'archiving' ? 'Archiving…' : 'Archive'}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const shellStyle = { padding: '28px 24px', maxWidth: 960, margin: '0 auto', fontFamily: 'Open Sans, sans-serif', color: '#2b1d14', minHeight: '60vh' };
const headerRowStyle = { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' };
const titleStyle = { fontFamily: 'Montserrat, sans-serif', fontSize: 28, fontWeight: 800, color: '#2b1d14', margin: 0 };
const subtitleStyle = { color: '#7b6b61', fontSize: 14, marginTop: 4 };
const headerActionsStyle = { display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' };

const OHUB_BTN_BASE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 40,
  padding: '10px 16px',
  borderRadius: 10,
  fontWeight: 700,
  fontSize: 13,
  lineHeight: 1,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  textAlign: 'center',
};

const btnPrimary = { ...OHUB_BTN_BASE, background: '#5B4A42', border: '1.5px solid #5B4A42', color: '#FFFFFF' };
const btnSecondary = { ...OHUB_BTN_BASE, background: '#FFFFFF', border: '1.5px solid #D9C8BC', color: '#5B4A42' };
const btnDisabled = { ...OHUB_BTN_BASE, background: '#F4EEE8', border: '1.5px solid #E5D8CD', color: '#B7A79E', cursor: 'not-allowed' };

const attentionStripStyle = { background: '#FFF5F5', border: '1.5px solid #f5c2bd', borderRadius: 12, marginBottom: 16, overflow: 'hidden' };
const attentionHeaderStyle = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', background: '#FDECEA', borderBottom: '1px solid #f5c2bd', flexWrap: 'wrap' };
const attentionTitleStyle = { fontWeight: 800, fontSize: 13, color: '#7b2a2a', fontFamily: 'Montserrat, sans-serif' };

const controlBarStyle = {
  display: 'flex', flexDirection: 'column', gap: 10,
  background: '#fff', border: '1.5px solid #e8e0d5', borderRadius: 12,
  padding: '12px 16px', marginBottom: 0,
  position: 'sticky', top: 0, zIndex: 40,
  boxShadow: '0 2px 8px rgba(43,29,20,0.07)',
};
const searchWrapStyle = { display: 'flex', alignItems: 'center', gap: 8, background: '#fdfaf8', border: '1.5px solid #e8e0d5', borderRadius: 8, padding: '0 10px' };
const searchIconStyle = { fontSize: 14, color: '#a09080', flexShrink: 0 };
const searchInputStyle = { flex: 1, border: 'none', background: 'transparent', outline: 'none', padding: '9px 0', fontSize: 14, color: '#2b1d14', fontFamily: 'inherit', minWidth: 0 };
const searchClearBtn = { background: 'none', border: 'none', cursor: 'pointer', color: '#9a8a84', fontSize: 16, padding: '0 2px', lineHeight: 1 };
const sortRowStyle = { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' };
const sortPillBtn = (active) => ({
  padding: '4px 12px', borderRadius: 20,
  border: active ? '1.5px solid #5A4A42' : '1.5px solid #d5c9c0',
  cursor: 'pointer', fontWeight: active ? 700 : 500, fontSize: 12,
  background: active ? '#5A4A42' : '#fff', color: active ? '#fff' : '#5A4A42',
  lineHeight: 1, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center',
});

const chipRowStyle = {
  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap',
  background: '#fff', paddingTop: 8, paddingBottom: 4, paddingLeft: 16, paddingRight: 16,
  position: 'sticky', top: 'var(--control-bar-height, 110px)', zIndex: 39,
};
const statusChipBtn = (active) => ({
  padding: '5px 13px', borderRadius: 20,
  border: active ? '1.5px solid #3D6B5E' : '1.5px solid #d5c9c0',
  cursor: 'pointer', fontWeight: active ? 700 : 500, fontSize: 12,
  background: active ? '#3D6B5E' : '#fff', color: active ? '#fff' : '#5A4A42',
  lineHeight: 1, fontFamily: 'inherit',
});

const listContainerStyle = { display: 'flex', flexDirection: 'column', gap: 0 };
const loadingStyle = { color: '#7b6b61', padding: '24px 0', textAlign: 'center', fontSize: 14 };
const errorStyle = { color: '#b71c1c', background: '#FDECEA', borderRadius: 8, padding: '12px 16px', marginBottom: 12, fontSize: 13 };
const retryBtn = { background: 'none', border: 'none', color: '#b71c1c', cursor: 'pointer', fontWeight: 700, textDecoration: 'underline', marginLeft: 8, fontFamily: 'inherit' };
const emptyStateStyle = { padding: '40px 20px', textAlign: 'center', color: '#7b6b61', background: '#fff', border: '1.5px dashed #e8e0d5', borderRadius: 12, fontSize: 14 };
const endOfListStyle = { padding: '16px 0', textAlign: 'center', color: '#a09080', fontSize: 12, fontStyle: 'italic' };

const rowMain = { flex: 1, minWidth: 0 };
const rowTopLine = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 };
const rowMeta = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#7b6b61', flexWrap: 'wrap' };
const rowActions = { display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' };
const actionBtnPrimary = { ...BTN_PRIMARY, padding: '7px 14px', fontSize: 12, minHeight: 32 };
const actionBtnSecondary = { ...BTN_SECONDARY, padding: '7px 14px', fontSize: 12, fontWeight: 600, minHeight: 32 };
const vendorNameStyle = { fontWeight: 700, fontSize: 14, color: '#2b1d14' };
const problemRowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 18px', borderBottom: '1px solid #fde0de', flexWrap: 'wrap' };
const problemChip = { background: '#FDECEA', color: '#b71c1c', borderRadius: 6, padding: '2px 7px', fontSize: 10, fontWeight: 700 };

const backBarStyle = { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', background: '#fdfaf8', borderBottom: '1px solid #e8e0d5' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#5A4A42', fontWeight: 700, fontSize: 13, padding: 0, fontFamily: 'inherit' };
const backCrumbStyle = { color: '#7b6b61', fontSize: 13 };

const modalOverlayStyle = { position: 'fixed', inset: 0, background: 'rgba(43,29,20,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)', paddingLeft: 20, paddingRight: 20 };
const modalBoxStyle = { background: '#fff', borderRadius: 14, padding: '24px', width: '100%', maxWidth: 440, boxShadow: '0 8px 40px rgba(43,29,20,0.20)', fontFamily: 'Open Sans, sans-serif' };
const modalCancelBtn = { ...BTN_SECONDARY, fontWeight: 600 };
const modalConfirmBtn = { ...BTN_PRIMARY, border: '1.5px solid' };

// ---------------------------------------------------------------------------
// Purchasing workspace styles
// ---------------------------------------------------------------------------
const purchasingHeaderStyle = { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' };
const purchasingTitleBlock = { flex: 1, minWidth: 220 };
const purchasingTitleStyle = { fontFamily: 'Montserrat, sans-serif', fontSize: 32, fontWeight: 800, color: '#2b1d14', margin: 0, letterSpacing: '-0.02em' };
const purchasingSubtitleStyle = { color: '#7b6b61', fontSize: 14, marginTop: 6, lineHeight: 1.4 };
const storeNameStyle = { color: '#a09080', fontSize: 12, marginTop: 4, fontWeight: 600 };
const purchasingDomainNav = { display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8, marginBottom: 18 };
const purchasingDomainLink = { display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', alignItems: 'center', gap: 9, minHeight: 72, padding: '10px 11px', border: '1.5px solid #E9DFD6', borderRadius: 11, background: '#fff', color: '#5A4A42', textDecoration: 'none', boxSizing: 'border-box' };
const purchasingDomainLinkActive = { ...purchasingDomainLink, borderColor: '#C9B9A9', background: '#FDF6EC' };
const purchasingDomainIcon = { fontSize: 21, lineHeight: 1 };
const purchasingDomainTitle = { display: 'block', color: '#3D2518', fontSize: 12, lineHeight: 1.25 };
const purchasingDomainDescription = { display: 'block', marginTop: 2, color: '#8B7B73', fontSize: 10, lineHeight: 1.3 };
const purchasingTabBar = { display: 'flex', gap: 6, marginBottom: 18, borderBottom: '1.5px solid #E9DFD6', paddingBottom: 6 };
const purchasingTabBtn = (active) => ({
  padding: '8px 18px',
  borderRadius: 999,
  border: active ? '1.5px solid #5A4A42' : '1.5px solid #E9DFD6',
  background: active ? '#5A4A42' : '#fff',
  color: active ? '#fff' : '#5A4A42',
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
});
const tabCountBadge = (active) => ({
  background: active ? 'rgba(255,255,255,0.22)' : '#FDF6EC',
  color: active ? '#fff' : '#5A4A42',
  borderRadius: 999,
  padding: '1px 7px',
  fontSize: 11,
  fontWeight: 700,
  minWidth: 18,
  textAlign: 'center',
});
const activePanelStyle = { display: 'flex', flexDirection: 'column', gap: 18 };
const historyPanelStyle = { display: 'flex', flexDirection: 'column', gap: 0 };
const activeSearchWrap = { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid #E9DFD6', borderRadius: 10, padding: '0 12px', marginBottom: 2 };
const activeSearchInput = { flex: 1, border: 'none', background: 'transparent', outline: 'none', padding: '10px 0', fontSize: 14, color: '#2b1d14', fontFamily: 'inherit', minWidth: 0 };
const stageSectionWrap = { background: '#fff', border: '1.5px solid #E9DFD6', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(43,29,20,0.04)' };
const stageHeaderRow = { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, borderBottom: '1px solid #F5EEE8', paddingBottom: 8 };
const stageTitleStyle = { fontFamily: 'Montserrat, sans-serif', fontSize: 16, fontWeight: 800, color: '#2b1d14', margin: 0, textTransform: 'none' };
const stageCountStyle = { fontSize: 13, fontWeight: 700, color: '#5A4A42', background: '#FDF6EC', borderRadius: 999, padding: '2px 9px', minWidth: 22, textAlign: 'center' };
const stageCardsWrap = { display: 'flex', flexDirection: 'column', gap: 10 };
const stageEmptyStyle = { padding: '16px', textAlign: 'center', color: '#8B7B73', fontSize: 13, background: '#FDF6EC', borderRadius: 8 };
const stageEmptyMuted = { padding: '8px', textAlign: 'center', color: '#a09080', fontSize: 12, fontStyle: 'italic' };
const recentHeaderWrap = { display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 };
const viewHistoryLinkWrap = { textAlign: 'center', marginTop: 6 };
const viewHistoryLink = { background: 'none', border: 'none', color: '#5A4A42', fontWeight: 700, fontSize: 13, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' };
const caughtUpCard = { background: '#FDF6EC', border: '1.5px solid #EAD9BE', borderRadius: 12, padding: '20px 18px', textAlign: 'center' };
const caughtUpTitle = { fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: 16, color: '#2b1d14', marginBottom: 4 };
const caughtUpSub = { color: '#7b6b61', fontSize: 13 };
const caughtUpHint = { color: '#a09080', fontSize: 11, marginTop: 6, fontStyle: 'italic' };
const secondaryActionsWrap = { display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' };
const secondaryLinkBtn = { background: 'none', border: '1.5px solid #D9C8BC', borderRadius: 999, padding: '8px 16px', fontWeight: 700, fontSize: 12, color: '#5A4A42', cursor: 'pointer', fontFamily: 'inherit' };
const secondaryHint = { color: '#a09080', fontSize: 12, fontStyle: 'italic' };
const historyShoppingSection = { marginTop: 22, padding: '16px', background: '#FBF8F5', border: '1.5px solid #E9DFD6', borderRadius: 12 };
const historySectionTitle = { fontWeight: 800, fontSize: 13, color: '#2b1d14', marginBottom: 4 };
const historySectionSub = { fontSize: 12, color: '#8B7B73', marginBottom: 10, lineHeight: 1.4 };
const legacyAttentionNote = { paddingTop: 8 };

// Activity card
const activityCardStyle = (stage) => ({
  display: 'flex',
  flexDirection: 'column',
  background: stage === ACTIVITY_STAGE.NEEDS_ATTENTION ? '#FFF5F5' : '#fff',
  border: stage === ACTIVITY_STAGE.NEEDS_ATTENTION ? '1.5px solid #f5c2bd' : '1.5px solid #E9DFD6',
  borderRadius: 12,
  padding: '14px 16px',
  gap: 6,
  boxShadow: '0 1px 4px rgba(43,29,20,0.04)',
});
const activityTopRow = { display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' };
// Wide enough to hold the two fact pills side by side. Below this the action
// button wraps to its own line instead of squeezing the pills into a column —
// a stacked "2 items" over "$50.48 estimated" breaks the alignment the pills
// exist for.
const activityVendorBlock = { flex: 1, minWidth: 264 };
const activityTitle = { fontWeight: 700, fontSize: 15, color: '#2b1d14', lineHeight: 1.3 };
const activitySubtitle = { fontSize: 12, color: '#8B7B73', marginTop: 2, lineHeight: 1.4 };
const activityBadgeRow = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 };
const activityMetaRow = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 };
const activityCreatedMeta = { fontSize: 11, color: '#a09080', marginTop: 6, lineHeight: 1.4 };

// Fixed-width pills. The width is the point: the same two columns land at the
// same x on every card, so the eye (or a thumb) tracks straight down them.
const PILL_COUNT = { bg: '#f2ece5', fg: '#5A4A42' };
const PILL_ESTIMATED = { bg: '#FFF4E0', fg: '#8A5A00' };
const PILL_SETTLED = { bg: '#E8F5E9', fg: '#2E7D32' };
const factPill = (colors, width) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: width, padding: '4px 10px', borderRadius: 999,
  background: colors.bg, color: colors.fg, border: `1px solid ${colors.fg}22`,
  fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
});
// An estimate and a settled amount must not look alike — the shopping trip's
// "$187.50 estimated" is a guess, a received PO's total is not. A card with no
// money on it (a count waiting to be reviewed) keeps the pill for the sake of
// the column, but in the neutral tone: an empty amber pill would read as a
// figure that failed to load.
function amountPillColors(activity) {
  if (!activity.amount) return PILL_COUNT;
  return activity.amount_kind === AMOUNT_KIND.ACTUAL || activity.amount_kind === AMOUNT_KIND.VERIFIED
    ? PILL_SETTLED
    : PILL_ESTIMATED;
}

const activityActionBlock = { display: 'flex', alignItems: 'center', flexShrink: 0 };
// One width for every card's primary action, whatever it says — "Review &
// send", "Start shopping", "Review purchases" — so the column of buttons is a
// column and not a ragged edge.
const activityPrimaryBtn = (stage) => ({
  ...BTN_PRIMARY,
  padding: '9px 16px',
  fontSize: 13,
  minHeight: 44,
  minWidth: 176,
  justifyContent: 'center',
  background: stage === ACTIVITY_STAGE.NEEDS_ATTENTION ? '#b71c1c' : '#5A4A42',
  borderColor: stage === ACTIVITY_STAGE.NEEDS_ATTENTION ? '#b71c1c' : '#5A4A42',
  whiteSpace: 'nowrap',
});
const badgeStyle = (bg, fg) => ({
  background: bg,
  color: fg,
  borderRadius: 999,
  padding: '2px 8px',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
});
const attentionExplainBox = { marginTop: 8, background: '#FFF5F5', border: '1px solid #f5c2bd', borderRadius: 8, padding: '8px 10px' };
const attentionExplainWhat = { fontSize: 12, fontWeight: 700, color: '#7b2a2a' };
const attentionExplainWhy = { fontSize: 11, color: '#8B7B73', marginTop: 2, lineHeight: 1.4 };
const activityFooter = { fontSize: 11, color: '#a09080', marginTop: 4, borderTop: '1px dashed #E9DFD6', paddingTop: 6 };

const mobileCss = `
  ${FOCUS_RING_CSS}
  @media (max-width: 680px) {
  .ohub-shell {
    padding: 10px 12px 24px !important;
  }
  .ohub-title {
    font-size: 22px !important;
  }
  .ohub-subtitle {
    font-size: 13px !important;
  }
  .ohub-purchasing-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    margin-bottom: 12px !important;
  }
  .ohub-domain-nav {
    grid-template-columns: 1fr !important;
    gap: 7px !important;
    margin-bottom: 16px !important;
  }
  .ohub-domain-link {
    min-height: 60px !important;
    padding: 9px 12px !important;
  }
  .ohub-domain-link strong { font-size: 14px !important; }
  .ohub-domain-link small { font-size: 12px !important; }
  .ohub-purchasing-tabs button {
    flex: 1 !important;
    justify-content: center !important;
    min-height: 44px !important;
  }
  .purchasing-activity-card {
    padding: 12px 14px !important;
  }
  .purchasing-activity-card > div {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .purchasing-activity-card button {
    width: 100% !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }
  .ohub-header-row {
    flex-direction: column !important;
    align-items: flex-start !important;
    margin-bottom: 12px !important;
  }
  .ohub-header-actions {
    width: 100% !important;
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 8px !important;
  }
  .ohub-header-actions button,
  .ohub-header-actions a {
    width: 100% !important;
    min-height: 44px !important;
    font-size: 13px !important;
    justify-content: center !important;
    box-sizing: border-box !important;
  }
  .ohub-attention-strip {
    margin-bottom: 12px !important;
  }
  .ohub-control-bar {
    padding: 10px 12px !important;
  }
  .ohub-search-input {
    font-size: 16px !important;
  }
  .ohub-sort-label, .ohub-chip-label {
    font-size: 11px !important;
  }
  .ohub-sort-row {
    gap: 4px !important;
    overflow-x: auto !important;
    flex-wrap: nowrap !important;
    -webkit-overflow-scrolling: touch !important;
    scrollbar-width: none !important;
    padding-bottom: 2px !important;
  }
  .ohub-sort-row::-webkit-scrollbar { display: none !important; }
  .ohub-sort-row button {
    font-size: 12px !important;
    flex-shrink: 0 !important;
    min-height: 36px !important;
  }
  .ohub-chip-row {
    gap: 5px !important;
    flex-wrap: nowrap !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
    scrollbar-width: none !important;
    padding-bottom: 4px !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
  }
  .ohub-chip-row::-webkit-scrollbar { display: none !important; }
  .ohub-chip-row > div {
    display: flex !important;
    flex-shrink: 0 !important;
    gap: 5px !important;
  }
  .ohub-chip-row button {
    font-size: 13px !important;
    flex-shrink: 0 !important;
    min-height: 36px !important;
  }
  .ohub-empty-state {
    font-size: 14px !important;
  }
  .ohub-back-bar {
    padding: 10px 14px !important;
  }
  .ohub-problem-actions {
    width: 100% !important;
    flex-wrap: nowrap !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
    scrollbar-width: none !important;
    padding-bottom: 4px !important;
    margin-top: 6px !important;
  }
  .ohub-problem-actions::-webkit-scrollbar {
    display: none !important;
  }
  .ohub-problem-actions > button {
    flex-shrink: 0 !important;
    white-space: nowrap !important;
    min-height: 36px !important;
  }
}
`;
